import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { ImageField } from '../ui/ImageField';
import { ProductPicker } from '../ui/ProductPicker';
import { ContentEditor } from '../ui/ContentEditor';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { NumberInput } from '../ui/NumberInput';
import { CheckList } from '../ui/CheckList';
import { ViewOnSite } from './ViewOnSite';
import { useEditorItem } from './useEditorItem';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { routineSeoChecks } from './catalogSeo';
import { routineSeoAuto } from '../../utils/seoAuto';
import { nextTierIn, tierIn } from '../../data/gifts';
import { skinTypes } from '../../data/concerns';
import { emptyRoutine, oneOf, routineChecks, routineLevels } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import { euro } from '../../core/format/currency';
const back = {
  to: '/be/protocollen',
  label: 'Alle protocollen'
};
const levelLabel: Record<(typeof routineLevels)[number], string> = {
  Start: 'Startprotocol (3–4 producten)',
  Compleet: 'Compleet protocol'
};

/** "Wat erbij zit" komt als lijst uit de ContentEditor; alleen tekstregels tellen. */
const asLines = (value: unknown): string[] => Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** Eén protocol: verhaal, producten in volgorde, wat erbij zit en de prijs (automatisch). */
export function RoutineEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    index,
    site,
    putItem,
    removeItem
  } = useAdmin();
  const {
    isNew,
    item: routine,
    set
  } = useEditorItem('routines', id, emptyRoutine);
  if (!routine) return <MissingItem title="Protocol niet gevonden" back={back} data-id="element-841" data-mp="4t8latp1n3rb" />;
  const totals = index.routineTotals(routine);
  // De staffel uit het concept: een wijziging in Instellingen is hier meteen te zien.
  const gift = tierIn(site.giftTiers, totals.price);
  const next = nextTierIn(site.giftTiers, totals.price);
  const covered = skinTypes.filter(s => totals.items.some(p => p.skinTypes.includes(s)));
  const usedBy = catalog.concerns.filter(c => c.routineId === routine.id);
  const add = () => {
    const created = {
      ...routine,
      id: uniqueSlug(slugify(routine.name), catalog.routines.map(r => r.id))
    };
    putItem('routines', created);
    navigate(`/be/protocollen/${created.id}`, {
      replace: true
    });
  };
  return <div data-id="element-842" data-mp="8vvdwwtjgspk">
      <PageHeader title={isNew ? 'Nieuw protocol' : routine.name} back={back} actions={isNew ? <button type="button" onClick={add} disabled={!routine.name.trim()} className={btnPrimary} data-id="element-844" data-mp="16ckfqqihnuyy">
              Protocol toevoegen
            </button> : <>
              <DeleteButton undoable blockedReason={usedBy.length ? `Kan nog niet: gekoppeld aan huidthema ${usedBy.map(c => c.label).join(', ')}. Kies daar eerst een ander protocol.` : undefined} onConfirm={() => {
        const restore = removeItem('routines', routine.id);
        navigate(back.to);
        undoToast(`“${routine.name || 'Naamloos protocol'}” verwijderd`, restore, {
          description: 'Pas definitief als je opslaat.',
          restored: 'Protocol teruggezet'
        });
      }} data-id="element-845" data-mp="19u82i6ons10m" />
              <ViewOnSite path="/routines" data-id="element-846" data-mp="21lxhfuyqg6zp" />
            </>} data-id="element-843" data-mp="8u29zpab8ml9" />

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-847" data-mp="26ks79gv4avtb">
        <div className="space-y-6 lg:col-span-8" data-id="element-848" data-mp="2bijzyd3l6lyk">
          <Panel title="Over dit protocol" data-id="element-849" data-mp="2bfo36s3h63bp">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-850" data-mp="2gg2bmb8znfa9">
              <Field label="Naam" className="sm:col-span-2" data-id="element-851" data-mp="2gehfwiaxtqat">
                <input value={routine.name} onChange={e => set({
                name: e.target.value
              })} className={inputCls} data-id="element-852" data-mp="3d6xngor4yhni" />
              </Field>
              <Field label="Kernzin" hint="Eén zin op de protocolkaart." className="sm:col-span-2" data-id="element-853" data-mp="387bbein1n7ga">
                <input value={routine.claim} onChange={e => set({
                claim: e.target.value
              })} className={inputCls} data-id="element-854" data-mp="3mzv0wgh9tgf9" />
              </Field>
              <Field label="Voor wie" className="sm:col-span-2" data-id="element-855" data-mp="2u8l306urxlcm">
                <input value={routine.forWho} onChange={e => set({
                forWho: e.target.value
              })} className={inputCls} data-id="element-856" data-mp="2ub7iqtrtj75x" />
              </Field>
              <Field label="Het verhaal" hint="Waarom deze producten samen werken." className="sm:col-span-2" data-id="element-857" data-mp="33b43ikb37lvp">
                <textarea value={routine.story} onChange={e => set({
                story: e.target.value
              })} rows={4} className={`${inputCls} resize-y leading-relaxed`} data-id="element-858" data-mp="2z7o97umhpxm6" />
              </Field>
              <Field label="Soort" data-id="element-859" data-mp="ej05fm2syyb7">
                <select value={routine.level} onChange={e => {
                const level = oneOf(routineLevels, e.target.value);
                if (level) set({
                  level
                });
              }} className={inputCls} data-id="element-860" data-mp="ehyvxm1fqjvl">
                  {routineLevels.map(l => <option key={l} value={l} data-id="element-861" data-mp="h2eszq6e4xm2">
                      {levelLabel[l]}
                    </option>)}
                </select>
              </Field>
              <div className="sm:col-span-2" data-id="element-862" data-mp="14uljw9tckce3">
                <Toggle checked={Boolean(routine.featured)} onChange={featured => set({
                featured
              })} label="Uitgelicht op de homepage" description="De eerste twee uitgelichte protocollen staan op de homepage. Een compleet protocol staat ook bovenaan de protocollenpagina." data-id="element-863" data-mp="19qakmgh3rzbz" />
              </div>
              <Field label="Hoort bij behandeling" hint="Wordt aangeboden na die afspraak." data-id="element-864" data-mp="1si3dmaw69kjp">
                <select value={routine.treatmentSlug ?? ''} onChange={e => set({
                treatmentSlug: e.target.value || undefined
              })} className={inputCls} data-id="element-865" data-mp="22ebwykv7xy01">
                  <option value="" data-id="element-866" data-mp="1xcmkd0sclc9u">Geen</option>
                  {catalog.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-867" data-mp="1tf8enj3vlmml">
                      {t.name}
                    </option>)}
                </select>
              </Field>
              <Field label="Gaat minimaal mee (maanden)" data-id="element-868" data-mp="1jgis3d11be00">
                <NumberInput value={routine.lasts.minMonths} onChange={v => v !== undefined && set({
                lasts: {
                  ...routine.lasts,
                  minMonths: v
                }
              })} min={1} max={24} data-id="element-869" data-mp="1enpo4rde49oz" />
              </Field>
              <Field label="Gaat maximaal mee (maanden)" hint={routine.lasts.maxMonths < routine.lasts.minMonths ? 'Lager dan het minimum: de site toont dan alleen het minimum.' : undefined} data-id="element-870" data-mp="10ogker6df81w">
                <NumberInput value={routine.lasts.maxMonths} onChange={v => v !== undefined && set({
                lasts: {
                  ...routine.lasts,
                  maxMonths: v
                }
              })} min={1} max={24} data-id="element-871" data-mp="37bnoj9sieszw" />
              </Field>
            </div>
          </Panel>

          <Panel title="Producten, in volgorde van gebruik" description="Reinigen → toner → serum → crème → SPF. Verplaats met de pijltjes." data-id="element-872" data-mp="3lb6xg4t5a8d9">
            <ProductPicker value={routine.productIds} onChange={productIds => set({
            productIds
          })} products={catalog.products} ordered data-id="element-873" data-mp="2tlbsx0wpuc40" />
          </Panel>

          <Panel title="Wat er zonder extra kosten bij zit" description="Nooit een korting — wel begeleiding en papier." data-id="element-874" data-mp="2xm46258pl15k">
            <ContentEditor value={routine.included} onChange={v => set({
            included: asLines(v)
          })} fieldKey="included" data-id="element-875" data-mp="32fqhju34yyzs" />
          </Panel>

          <SeoPanel seo={routine.seo} onChange={seo => set({
          seo
        })} auto={routineSeoAuto(routine)} path={null} imageFallback="het sfeerbeeld" data-id="element-876" data-mp="29wt96yuhbqwp" />
        </div>

        <aside aria-label="Protocolinstellingen" className="space-y-6 lg:col-span-4" data-id="element-877" data-mp="13c3ep0smfdqt">
          <Panel title="Prijs" data-id="element-878" data-mp="ycqp48ad7skj">
            <p className="font-display text-4xl font-light text-ink" data-id="element-879" data-mp="1d86mam68s4l5">{euro(totals.price)}</p>
            <p className="mt-1 text-[13px] text-muted" data-id="element-880" data-mp="1893i13y362e8">Som van {totals.items.length} producten, automatisch.</p>
            <p className="mt-4 border-t border-line pt-4 text-[13px] leading-relaxed text-muted" data-id="element-881" data-mp="1m8lem5hmijy5">
              {gift ? <>
                  Klant krijgt: <span className="text-ink" data-id="element-882" data-mp="1h9i8ehpwq7ol">{gift.title}</span>
                </> : 'Nog geen cadeautrede bereikt.'}
              {next && <>
                  <br data-id="element-883" data-mp="1r1xo18dqek6b" />
                  Nog {euro(next.threshold - totals.price)} tot {next.phrase}.
                </>}
            </p>
          </Panel>

          <Panel title="Huidthema’s die dit dekt" data-id="element-884" data-mp="20zc4h7znn8ym">
            <p className="text-[13px] leading-relaxed text-muted" data-id="element-885" data-mp="2jmb78eqbdyg6">{covered.length ? covered.join(', ') : 'Nog geen — voeg producten toe.'}</p>
            {usedBy.length > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-886" data-mp="2ois540nbfr6g">
                Aanbevolen bij: <span className="text-ink" data-id="element-887" data-mp="2al3lew4ziq2z">{usedBy.map(c => c.label).join(', ')}</span>
              </p>}
          </Panel>

          <Panel title="Sfeerbeeld" data-id="element-888" data-mp="20vjynfe5ic2u">
            <ImageField value={routine.image} onChange={image => set({
            image
          })} fit="cover" aspect="aspect-[4/5]" data-id="element-889" data-mp="20uinzc11rrln" />
          </Panel>

          <Panel title="Wat mist er nog?" data-id="element-890" data-mp="25od7ofgnk6kq">
            <CheckList checks={[...routineChecks(routine), ...routineSeoChecks(routine, catalog.routines)]} okText="Dit protocol is compleet" data-id="element-891" data-mp="1rvol53x4i4zn" />
          </Panel>
        </aside>
      </div>
    </div>;
}